import type { AssignableRole } from "@unbox/api";
import { ChevronDown } from "@untitledui/icons";
import {
  Button as AriaButton,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  Popover as AriaPopover,
  Select as AriaSelect,
} from "react-aria-components";
import { RoleOption, rolePillStyles, type ScopedRoleValue, scopedRoleLabels } from "@/components/admin/role-label";
import { cx } from "@/utils/cx";

// UNB-2001 — the single source of truth for the scoped-role dropdown pill.
// Every surface that lets staff pick a role (create workspace/merchant Users
// step, add-user-to-merchant, the create/edit-user role tree) renders THIS.
// The Figma colours live in `rolePillStyles` (role-label.tsx) so the read-only
// role badges in the user tables share exactly the same styling.

/** A concrete assignable role, or the derived "multiple" display state. */
export type RoleSelectValue = ScopedRoleValue;

type RoleSelectProps = {
  value: RoleSelectValue;
  onChange: (role: AssignableRole) => void;
  /** The roles selectable here — pass the scope constant (workspaceScopeRoles / merchantScopeRoles). */
  options: AssignableRole[];
  isDisabled?: boolean;
  "aria-label": string;
};

export const RoleSelect = ({ value, onChange, options, isDisabled, "aria-label": ariaLabel }: RoleSelectProps) => {
  const isMultiple = value === "multiple";
  const style = rolePillStyles[value];
  const label = scopedRoleLabels[value];
  // The pill can display a derived value that isn't selectable here (e.g. an
  // all-merchant-admin workspace summary when the workspace scope only offers
  // Workspace Admin / User). Show it on the button, but keep react-aria's
  // selection empty so it doesn't warn about a key missing from the options.
  const selectedKey = !isMultiple && options.includes(value) ? value : null;

  return (
    <AriaSelect
      aria-label={ariaLabel}
      selectedKey={selectedKey}
      isDisabled={isDisabled}
      onSelectionChange={(key) => {
        if (key) onChange(key as AssignableRole);
      }}
      className="w-fit"
    >
      <AriaButton
        className={cx(
          "flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium outline-hidden transition-colors",
          isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          style.badge,
        )}
      >
        <span className="whitespace-nowrap">{label}</span>
        <ChevronDown aria-hidden="true" className={cx("size-3.5 shrink-0", style.chevron)} />
      </AriaButton>

      <AriaPopover
        offset={4}
        placement="bottom end"
        // Portals to <body> (outside the admin `.dark-mode` wrapper), so it must
        // re-declare dark-mode to pick up the dark theme tokens.
        className="dark-mode pointer-events-auto w-80 overflow-auto rounded-lg bg-primary py-1 shadow-lg ring-1 ring-secondary_alt outline-hidden"
      >
        <AriaListBox className="outline-hidden">
          {options.map((role) => (
            <AriaListBoxItem
              key={role}
              id={role}
              textValue={scopedRoleLabels[role]}
              className={({ isFocused, isSelected }) =>
                cx(
                  "flex cursor-pointer items-start gap-2 px-3 py-2 outline-hidden",
                  isFocused && "bg-primary_hover",
                  isSelected && "bg-active",
                )
              }
            >
              {({ isSelected, isDisabled: itemDisabled }) => (
                <RoleOption role={role} isSelected={isSelected} isDisabled={itemDisabled} />
              )}
            </AriaListBoxItem>
          ))}
        </AriaListBox>
      </AriaPopover>
    </AriaSelect>
  );
};
